import React, { useEffect, useMemo, useState } from "react";
import Navbar from "../components/Navbar";
import SeoHead from "../components/seo/SeoHead";
import Footer from "../components/Footer";

const THEME_KEY = "hookora_theme";

function TermsPage() {
  const getStoredTheme = () => {
    if (typeof window === "undefined") return "light";
    return localStorage.getItem(THEME_KEY) || "light";
  };

  const [theme, setTheme] = useState(getStoredTheme());

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const syncTheme = () => {
      setTheme(localStorage.getItem(THEME_KEY) || "light");
    };

    window.addEventListener("storage", syncTheme);
    window.addEventListener("hookora-preferences-updated", syncTheme);

    return () => {
      window.removeEventListener("storage", syncTheme);
      window.removeEventListener("hookora-preferences-updated", syncTheme);
    };
  }, []);

  const isDark = theme === "dark";

  const colors = useMemo(
    () => ({
      pageBg: isDark ? "#020617" : "#f8fafc",
      pageGradient: isDark
        ? "radial-gradient(circle at top left, rgba(59,130,246,0.12), transparent 28%), radial-gradient(circle at top right, rgba(124,58,237,0.10), transparent 24%)"
        : "none",
      cardBg: isDark ? "rgba(15,23,42,0.78)" : "#ffffff",
      border: isDark ? "rgba(148,163,184,0.18)" : "#e5e7eb",
      text: isDark ? "#f8fafc" : "#111827",
      textMuted: isDark ? "#94a3b8" : "#6b7280",
      textSoft: isDark ? "#cbd5e1" : "#4b5563",
      badgeBg: isDark ? "rgba(88,28,135,0.18)" : "#f5f3ff",
      badgeText: isDark ? "#d8b4fe" : "#6d28d9",
      shadow: isDark
        ? "0 14px 40px rgba(2, 6, 23, 0.45)"
        : "0 10px 30px rgba(15, 23, 42, 0.05)"
    }),
    [isDark]
  );

  const sectionTitleStyle = {
    color: colors.text,
    marginBottom: "10px"
  };

  const paragraphStyle = {
    color: colors.textSoft,
    lineHeight: "1.8"
  };

  return (
    <div style={{ background: colors.pageBg, backgroundImage: colors.pageGradient, minHeight: "100vh" }}>
      <SeoHead
        title="Terms of Service | Hookora"
        description="Read Hookora's terms of service covering account use, subscriptions, billing, cancellations, and acceptable use."
        path="/terms"
      />

      <Navbar />

      <div
        style={{
          maxWidth: "900px",
          margin: "0 auto",
          padding: "32px 16px 56px"
        }}
      >
        <div
          style={{
            background: colors.cardBg,
            border: `1px solid ${colors.border}`,
            borderRadius: "24px",
            padding: "28px",
            boxShadow: colors.shadow,
            backdropFilter: isDark ? "blur(14px)" : "none"
          }}
        >
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "8px",
              padding: "8px 12px",
              borderRadius: "999px",
              background: colors.badgeBg,
              color: colors.badgeText,
              fontSize: "12px",
              fontWeight: "700",
              marginBottom: "14px",
              border: isDark ? `1px solid ${colors.border}` : "none"
            }}
          >
            Terms
          </div>

          <h1
            style={{
              marginTop: 0,
              marginBottom: "10px",
              color: colors.text,
              fontSize: "34px",
              lineHeight: "1.1"
            }}
          >
            Terms of Service
          </h1>

          <p
            style={{
              color: colors.textMuted,
              lineHeight: "1.7",
              marginBottom: "28px",
              fontSize: "15px"
            }}
          >
            These Terms of Service govern your access to and use of Hookora’s
            website, platform, tools, and related services.
          </p>

          <section style={{ marginBottom: "28px" }}>
            <h2 style={sectionTitleStyle}>
              Use of Hookora
            </h2>
            <p style={{ ...paragraphStyle, margin: 0 }}>
              By using Hookora, you agree to use the platform only for lawful and
              appropriate purposes. You are responsible for your account activity
              and for keeping your login credentials secure.
            </p>
          </section>

          <section style={{ marginBottom: "28px" }}>
            <h2 style={sectionTitleStyle}>
              Accounts
            </h2>
            <p style={{ ...paragraphStyle, margin: 0 }}>
              You may need to create an account to access certain Hookora features.
              You agree to provide accurate information and to keep your account
              details current.
            </p>
          </section>

          <section style={{ marginBottom: "28px" }}>
            <h2 style={sectionTitleStyle}>
              Plans and subscriptions
            </h2>
            <p style={{ ...paragraphStyle, margin: 0 }}>
              Hookora may offer free and paid plans. Paid plans are billed according
              to the pricing shown at checkout. Access to plan features depends on
              the subscription level connected to your account.
            </p>
          </section>

          <section style={{ marginBottom: "28px" }}>
            <h2 style={sectionTitleStyle}>
              Billing and cancellations
            </h2>
            <p style={{ ...paragraphStyle, marginBottom: "12px" }}>
              Paid subscriptions may renew automatically unless cancelled. If a
              subscription is cancelled, access continues until the end of the
              already-paid billing period unless otherwise stated.
            </p>
            <p style={{ ...paragraphStyle, margin: 0 }}>
              Refunds are governed by Hookora’s Refund & Cancellation Policy.
            </p>
          </section>

          <section style={{ marginBottom: "28px" }}>
            <h2 style={sectionTitleStyle}>
              Acceptable use
            </h2>
            <p style={{ ...paragraphStyle, marginTop: 0 }}>
              You agree not to misuse Hookora, disrupt the platform, attempt
              unauthorized access, or use the service in a way that harms the
              platform, other users, or third parties.
            </p>
          </section>

          <section style={{ marginBottom: "28px" }}>
            <h2 style={sectionTitleStyle}>
              Service changes
            </h2>
            <p style={{ ...paragraphStyle, margin: 0 }}>
              Hookora may update, modify, suspend, or improve parts of the service
              at any time in order to maintain, develop, or improve the platform.
            </p>
          </section>

          <section style={{ marginBottom: "28px" }}>
            <h2 style={sectionTitleStyle}>
              Limitation of liability
            </h2>
            <p style={{ ...paragraphStyle, margin: 0 }}>
              Hookora is provided on an as-available basis. While we work to keep
              the platform reliable and useful, we do not guarantee uninterrupted
              availability or that the service will always be free from errors.
            </p>
          </section>

          <section>
            <h2 style={sectionTitleStyle}>
              Updates to these terms
            </h2>
            <p style={{ ...paragraphStyle, margin: 0 }}>
              Hookora may update these Terms of Service from time to time. Continued
              use of the platform after updates means you accept the revised terms.
            </p>
          </section>
        </div>
      </div>

      <Footer />
    </div>
  );
}

export default TermsPage;